import { useEffect, useState } from "react";
import AppShell from "@/pages/home/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import * as api from "@/lib/api";
import type { StampHistoryItem } from "@/lib/types";
import BrandLogo from "@/components/BrandLogo";

/** 表示用: 2026.04.10 → 2026年4月10日 */
function formatHistoryDate(raw: string): string {
  const m = raw.match(/^(\d{4})\.(\d{1,2})\.(\d{1,2})$/);
  if (!m) return raw;
  return `${m[1]}年${Number(m[2])}月${Number(m[3])}日`;
}

function stampUnit(n: number): string {
  const a = Math.abs(n);
  if (!Number.isFinite(a) || a === 0) return "—";
  return `${a}スタンプ`;
}

/** API／旧JSONで stamps が欠ける場合のフォールバック */
function parseStampDelta(item: StampHistoryItem & { points?: number }): number | null {
  const s = item.stamps;
  if (typeof s === "number" && Number.isFinite(s) && s !== 0) return s;
  const p = item.points;
  if (typeof p === "number" && Number.isFinite(p)) {
    if (p > 0) return 1;
    if (p < 0) return -20;
  }
  return null;
}

export default function HistoryPage() {
  const { getIdToken, user } = useAuth();
  const userVersion = `${user?.stamps ?? 0}`;
  const [items, setItems] = useState<StampHistoryItem[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    void (async () => {
      const token = await getIdToken();
      if (!token) {
        if (!cancelled) {
          setLoadError("ログインが必要です");
          setLoading(false);
        }
        return;
      }
      try {
        const res = await api.fetchHistory(token);
        if (!cancelled) setItems(res.history);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : "読み込みに失敗しました");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getIdToken, user, userVersion]);

  return (
    <AppShell>
      <div className="px-4 py-5 space-y-5 max-w-lg mx-auto">
        <div>
          <h1 className="text-lg font-bold" style={{ color: "#f4e5c3", fontFamily: "Montserrat, sans-serif" }}>
            スタンプ履歴
          </h1>
          <p className="text-xs mt-0.5" style={{ color: "rgba(244,229,195,0.5)" }}>
            スタンプを貯めた日・使った日
          </p>
        </div>

        {loadError && (
          <p className="text-sm text-center" style={{ color: "#ff8080" }}>
            {loadError}
          </p>
        )}

        {loading && (
          <div className="flex items-center justify-center py-6">
            <BrandLogo compact />
          </div>
        )}

        <div
          className="rounded-2xl overflow-hidden"
          style={{
            border: "1px solid rgba(212,175,55,0.2)",
            background: "linear-gradient(135deg, rgba(26,47,47,0.5) 0%, rgba(13,31,31,0.5) 100%)",
          }}
        >
          {!loading &&
            items
              .map((item) => {
                const delta = parseStampDelta(item);
                return delta === null ? null : { item, delta };
              })
              .filter((row): row is { item: StampHistoryItem; delta: number } => row !== null)
              .map((row, idx, rows) => {
                const { item, delta } = row;
                const isEarn = delta > 0;
                return (
                  <div
                    key={item.id}
                    className="flex items-center justify-between gap-4 px-4 py-3.5"
                    style={{
                      borderBottom:
                        idx < rows.length - 1 ? "1px solid rgba(212,175,55,0.12)" : "none",
                    }}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium" style={{ color: "#f4e5c3" }}>
                        {formatHistoryDate(item.date)}
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: "rgba(244,229,195,0.45)" }}>
                        {isEarn ? "スタンプ獲得" : "スタンプ消費"}
                      </p>
                    </div>
                    <p
                      className="text-sm font-bold tabular-nums flex-shrink-0 whitespace-nowrap"
                      style={{
                        color: isEarn ? "#d4af37" : "#ff8080",
                        fontFamily: "Montserrat, sans-serif",
                      }}
                    >
                      {isEarn ? "+" : "−"}
                      {stampUnit(delta)}
                    </p>
                  </div>
                );
              })}
        </div>

        {!loading && items.length === 0 && (
          <div className="text-center py-12">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
              <i className="ri-inbox-line text-4xl" style={{ color: "rgba(212,175,55,0.3)" }}></i>
            </div>
            <p className="text-sm" style={{ color: "rgba(244,229,195,0.4)" }}>
              履歴がありません
            </p>
          </div>
        )}
      </div>
    </AppShell>
  );
}
